import { Icon } from 'components/common';
import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  exportTemplateActions,
  exportTemplateSelectors as selectors,
} from 'reducers/exportTemplate/reducer';
import { useUpdateTemplateMutation } from '../hooks/useUpdateTemplateMutation';
import { ExportTemplateButton, ModalFooter, ModalList } from '../styled';
import ColumnEditor from './ColumnEditor';
import { templateEditor as strings } from '../strings';
import { ExportTemplateModalHeader } from './ExportTemplateModalHeader';
import { TemplateSheetRow } from './TemplateSheetRow';

/**
 * Editor for creating or editing an export template.
 * Shows list of sheets with toggles and field counts.
 */
function TemplateEditor() {
  const dispatch = useDispatch();

  // ========== Redux state ==========

  const sheets = useSelector(selectors.getSheetsMap);
  const sheetOrder = useSelector(selectors.getSheetOrder);
  const oneTimeExportAvailable = useSelector(selectors.oneTimeExportAvailable);
  const existingtemplateId = useSelector(selectors.getExistingTemplateId);
  const doesUserOwnEditingTemplate = useSelector(
    selectors.doesUserOwnEditingTemplate
  );
  const sheetsPayload = useSelector(selectors.getEditingSheetsPayload);
  const editingSheet = useSelector(selectors.getDraftSheet);

  // ========== Local UI state ==========

  const [draggedId, setDraggedId] = useState(null);
  const lastDragKeyRef = useRef(null);

  // ========== Event handlers ==========

  const { triggerUpdate, updateLoading } = useUpdateTemplateMutation(() => {
    dispatch(exportTemplateActions.editModalClosed());
  });

  const handleSaveNew = () => {
    dispatch(
      exportTemplateActions.templateSaveInitiated({ context: 'new_template' })
    );
  };

  const handleOverwrite = () => {
    if (!existingtemplateId) return;
    triggerUpdate(existingtemplateId, { sheets: sheetsPayload });
  };

  const handleToggleSheet = (sheetId, included) => {
    dispatch(exportTemplateActions.editingSheetToggled({ sheetId, included }));
  };

  const onOneTimeExportButtonPress = () => {
    dispatch(exportTemplateActions.oneTimeExportTriggered());
  };

  const handleEditColumns = sheetId => {
    dispatch(exportTemplateActions.columnEditingStarted(sheetId));
  };

  const handleConfirmColumnEditor = () => {
    dispatch(exportTemplateActions.columnEditingConfirmed());
  };

  const handleCancelColumnEditor = () => {
    dispatch(exportTemplateActions.columnEditingCancelled());
  };

  const handleDragStart = (e, sheetId) => {
    setDraggedId(sheetId);
    lastDragKeyRef.current = null;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', sheetId);
  };

  const handleDragOver = (e, targetSheetId) => {
    e.preventDefault();
    if (draggedId === null || draggedId === targetSheetId) return;

    const fromIndex = sheetOrder.indexOf(draggedId);
    const toIndex = sheetOrder.indexOf(targetSheetId);
    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return;

    const dragKey = `${draggedId}:${targetSheetId}:${fromIndex}:${toIndex}`;
    if (lastDragKeyRef.current === dragKey) return;
    lastDragKeyRef.current = dragKey;

    const newOrder = [...sheetOrder];
    const [movedId] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, movedId);
    dispatch(exportTemplateActions.sheetOrderChanged(newOrder));
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    lastDragKeyRef.current = null;
  };

  // ========== Render Column Editor ==========

  if (editingSheet) {
    return (
      <ColumnEditor
        onConfirm={handleConfirmColumnEditor}
        onCancel={handleCancelColumnEditor}
      />
    );
  }

  // ========== Render Sheet List ==========

  return (
    <div data-testid='et-modal-template-editor-view'>
      <ExportTemplateModalHeader />
      <ModalList>
        {sheetOrder.map(sheetId => {
          const sheet = sheets[sheetId];
          return (
            <TemplateSheetRow
              key={sheet.sheetMetadataId}
              sheet={sheet}
              draggedId={draggedId}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              onToggleSheet={handleToggleSheet}
              onEditColumns={handleEditColumns}
            />
          );
        })}
      </ModalList>

      <ModalFooter>
        {oneTimeExportAvailable && (
          <ExportTemplateButton
            data-testid='et-modal-one-time-export-button'
            onClick={onOneTimeExportButtonPress}
          >
            <Icon icon='download' size='sm' color='#000000D9' />
            {strings.oneTimeExport}
          </ExportTemplateButton>
        )}
        {oneTimeExportAvailable || <div />}
        <div style={{ display: 'flex', gap: 20 }}>
          <ExportTemplateButton
            data-testid='et-modal-save-new-button'
            onClick={handleSaveNew}
          >
            {strings.saveNew}
          </ExportTemplateButton>
          {existingtemplateId && (
            <ExportTemplateButton
              data-testid='et-modal-update-button'
              type='primary'
              onClick={handleOverwrite}
              loading={updateLoading}
              disabled={!doesUserOwnEditingTemplate}
              title={doesUserOwnEditingTemplate ? '' : strings.cannotOverwrite}
            >
              {strings.update}
            </ExportTemplateButton>
          )}
        </div>
      </ModalFooter>
    </div>
  );
}

export default TemplateEditor;
